import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Chip,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'contexts/alertContext';
import { getUserData } from 'helpers';
import { getAllPermission } from 'helpers';
import BackButton from 'components/UI/BackButton';
import EmptyContent from 'components/EmptyContent';
import { Person } from '@mui/icons-material';
import {
  getWorkshopScoreDataAssessments,
  getWorkshopScoreDataEmployees,
  updateWorkshopScoreData,
  IWorkshopScoreDataAssessment,
  IWorkshopScoreDataEmployee,
  IWorkshopScoreDataAnswer,
  IWorkshopScoreDataOption,
  IUpdateWorkshopScoreDataRequest,
} from 'framework/workshopScoreData';
import { Feature } from 'models/api';

// Types
interface IAssessment {
  id: string;
  title: string;
  titleAr: string;
  typeName: string;
  typeNameAr: string;
}

interface IEmployeeAssessment {
  employeeId: string;
  employeeName: string;
  employeeNameAr: string;
  answers: IEmployeeAnswer[];
}

interface IEmployeeAnswer {
  questionId: string;
  questionTitle: string;
  questionTitleAr: string;
  questionOrder: number;
  correctAnswer: string;
  correctAnswerAr: string;
  selectedAnswer: string;
  selectedAnswerAr: string;
  options: IAnswerOption[];
}

interface IAnswerOption {
  id: string;
  description: string;
  descriptionAr: string;
  score: number;
  optionOrder: number;
}

interface Props {
  feature: Feature;
}

const WorkshopScoreData: React.FC<Props> = ({ feature }) => {
  const urlParams = useParams();
  let workshopId: string = urlParams[`${feature.singleName}Id`] as string;
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { setAlert } = useAlert();
  const userData = getUserData();
  const { colors } = userData;

  // State
  const [loading, setLoading] = useState<boolean>(true);
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const [assessments, setAssessments] = useState<IAssessment[]>([]);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState<string>('');
  const [employeeAssessments, setEmployeeAssessments] = useState<IEmployeeAssessment[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  // Check permissions
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const permissions = await getAllPermission();
        const scoreDataPermission = permissions?.find(
          (p) => p.permissionName === 'WorkshopScoreData'
        );
        const hasAccess = 
          scoreDataPermission?.hasReadPermission || 
          scoreDataPermission?.hasModifyPermission || 
          false;
        
        setHasPermission(hasAccess);
        
        if (!hasAccess) {
          setAlert({
            open: true,
            message: 'You do not have permission to access this page',
            type: 'error',
          });
          navigate('/workshops');
        }
      } catch (error) {
        console.error('Error checking permissions:', error);
        setAlert({
          open: true,
          message: 'Error checking permissions',
          type: 'error',
        });
        navigate('/workshops');
      }
    };

    checkPermissions();
  }, [navigate, setAlert]);

  // Fetch assessments
  useEffect(() => {
    if (hasPermission && workshopId) {
      fetchAssessments();
    }
  }, [hasPermission, workshopId]);

  const fetchAssessments = async () => {
    try {
      setLoading(true);
      const response = await getWorkshopScoreDataAssessments(workshopId!);
      setAssessments(response);
    } catch (error) {
      console.error('Error fetching assessments:', error);
      setAlert({
        open: true,
        message: 'Error fetching assessments',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeAssessments = async (assessmentId: string) => {
    try {
      setLoadingEmployees(true);
      const response = await getWorkshopScoreDataEmployees(workshopId!, assessmentId);
      setEmployeeAssessments(response);
    } catch (error) {
      console.error('Error fetching employee assessments:', error);
      setAlert({
        open: true,
        message: 'Error fetching employee assessments',
        type: 'error',
      });
    } finally {
      setLoadingEmployees(false);
    }
  };

  const handleAssessmentChange = (assessmentId: string) => {
    setSelectedAssessmentId(assessmentId);
    if (assessmentId) {
      fetchEmployeeAssessments(assessmentId);
    } else {
      setEmployeeAssessments([]);
    }
  };

  const handleAnswerChange = (
    employeeId: string,
    questionId: string,
    newAnswerId: string
  ) => {
    setEmployeeAssessments(prev => 
      prev.map(emp => 
        emp.employeeId === employeeId
          ? {
              ...emp,
              answers: emp.answers.map(answer =>
                answer.questionId === questionId
                  ? {
                      ...answer,
                      selectedAnswer: answer.options.find(opt => opt.id === newAnswerId)?.description || '',
                      selectedAnswerAr: answer.options.find(opt => opt.id === newAnswerId)?.descriptionAr || '',
                    }
                  : answer
              ),
            }
          : emp
      )
    );
  };

  const handleSaveChanges = async () => {
    try {
      setSaving(true);
      
      const updateData: IUpdateWorkshopScoreDataRequest = {
        workshopId: workshopId!,
        assessmentId: selectedAssessmentId,
        employeeAnswers: employeeAssessments.map(emp => ({
          employeeId: emp.employeeId,
          answers: emp.answers.map(answer => ({
            questionId: answer.questionId,
            selectedOptionId: answer.options.find(opt => opt.description === answer.selectedAnswer)?.id || '',
          })),
        })),
      };
      
      await updateWorkshopScoreData(updateData);
      
      setAlert({
        open: true,
        message: 'Assessment answers updated successfully',
        type: 'success',
      });
    } catch (error) {
      console.error('Error saving changes:', error);
      setAlert({
        open: true,
        message: 'Error saving changes',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const getDisplayText = (english: string, arabic: string) => {
    return i18n.language === 'ar' ? arabic : english;
  };

  const getOptionLetter = (index: number): string => {
    return String.fromCharCode(65 + index); // A, B, C, etc.
  };

  const getAnswerWithLetter = (answer: IEmployeeAnswer, answerText: string, answerTextAr: string): string => {
    const displayText = getDisplayText(answerText, answerTextAr);
    const optionIndex = answer.options.findIndex(opt => 
      opt.description === answerText || opt.descriptionAr === answerTextAr
    );
    if (optionIndex >= 0) {
      return `${getOptionLetter(optionIndex)}. ${displayText}`;
    }
    return displayText;
  };

  const isAnswerCorrect = (answer: IEmployeeAnswer) => {
    return answer.selectedAnswer === answer.correctAnswer;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!hasPermission) {
    return (
      <Alert severity="error">
        You do not have permission to access this page.
      </Alert>
    );
  }

  return (
    <Box p={2}>
      <BackButton path="/workshops" text={t("T_back_to_workshops")} />
      
      <Typography
        variant="h3"
        color={colors?.labelColor || "primary.main"}
        sx={{ mb: 3 }}
      >
        Workshop Score Data
      </Typography>

      {/* Assessment Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Select Assessment
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Assessment</InputLabel>
            <Select
              value={selectedAssessmentId}
              onChange={(e) => handleAssessmentChange(e.target.value)}
              label="Assessment"
            >
              {assessments.map((assessment) => (
                <MenuItem key={assessment.id} value={assessment.id}>
                  {getDisplayText(assessment.title, assessment.titleAr)} - {getDisplayText(assessment.typeName, assessment.typeNameAr)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Employee Assessment Grid */}
      {selectedAssessmentId && (
        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">
                Employee Assessment Results
              </Typography>
              <Button
                variant="contained"
                onClick={handleSaveChanges}
                disabled={saving || employeeAssessments.length === 0}
                startIcon={saving ? <CircularProgress size={16} /> : null}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Stack>

            {loadingEmployees ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
              </Box>
            ) : employeeAssessments.length > 0 ? (
              <TableContainer 
                component={Paper}
                sx={{
                  maxWidth: '100%',
                  overflowX: 'hidden',
                  overflowY: 'auto'
                }}
              >
                <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: '15%' }}>Employee Name</TableCell>
                      <TableCell sx={{ width: '25%' }}>Question</TableCell>
                      <TableCell sx={{ width: '15%' }}>Correct Answer</TableCell>
                      <TableCell sx={{ width: '15%' }}>Current Answer</TableCell>
                      <TableCell sx={{ width: '30%' }}>Change Answer</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {employeeAssessments.map((employee) =>
                      employee.answers.map((answer, index) => (
                        <TableRow key={`${employee.employeeId}-${answer.questionId}`}>
                          {index === 0 && (
                            <TableCell 
                              rowSpan={employee.answers.length}
                              sx={{ 
                                width: '15%',
                                verticalAlign: 'top',
                                fontWeight: 'bold',
                                backgroundColor: 'grey.50',
                                wordWrap: 'break-word'
                              }}
                            >
                              {getDisplayText(employee.employeeName, employee.employeeNameAr)}
                            </TableCell>
                          )}
                          <TableCell sx={{ width: '25%', wordWrap: 'break-word' }}>
                            <strong>{answer.questionOrder || (index + 1)}.</strong> {getDisplayText(answer.questionTitle, answer.questionTitleAr)}
                          </TableCell>
                          <TableCell sx={{ width: '15%', wordWrap: 'break-word' }}>
                            <Chip
                              label={getAnswerWithLetter(answer, answer.correctAnswer, answer.correctAnswerAr)}
                              color="success"
                              size="small"
                              sx={{ maxWidth: '100%' }}
                            />
                          </TableCell>
                          <TableCell sx={{ width: '15%', wordWrap: 'break-word' }}>
                            <Chip
                              label={getAnswerWithLetter(answer, answer.selectedAnswer, answer.selectedAnswerAr)}
                              color={isAnswerCorrect(answer) ? "success" : "error"}
                              size="small"
                              sx={{ maxWidth: '100%' }}
                            />
                          </TableCell>
                          <TableCell sx={{ width: '30%' }}>
                            <FormControl size="small" fullWidth>
                              <Select
                                value={answer.options.find(opt => opt.description === answer.selectedAnswer)?.id || ''}
                                onChange={(e) => handleAnswerChange(employee.employeeId, answer.questionId, e.target.value)}
                              >
                                {answer.options.map((option, optIndex) => (
                                  <MenuItem key={option.id} value={option.id}>
                                    <strong>{getOptionLetter(optIndex)}.</strong> {getDisplayText(option.description, option.descriptionAr)}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <EmptyContent
                icon={<Person sx={{ fontSize: "3.5rem" }} />}
                header="No Employee Assessments"
                message="No employees have taken this assessment yet."
              />
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default WorkshopScoreData;
